// src/components/UploadWorkspace/LocalWorkspace.js

import React, { useState, useRef, useEffect, useCallback } from 'react';
import './localWorkspace.css';

// FIREBASE
import { deleteObject } from 'firebase/storage';
import { db, auth } from '../../../../firebase';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';

// FUNCTIONS
import { uploadlocalWorkspace } from './uploadlocalWorkspace';

// IMPORT UI
import InsufficientTimePopUp from '../../../../components/miscComponents/InsufficientTimePopUp';
import PlanPurchasePage from '../../../../myPlans/PlanPurchasePage';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleArrowUp } from '@fortawesome/free-solid-svg-icons';

// IMAGES
import bumpups_white_small_logo from '../../../../assets/images/logo/bumpups-white-small-logo.png';
import processBumpupslogo from '../../../../assets/gifmedia/bumpups-2.gif';
import placeholder_profile from '../../../../assets/images/profile/placeholder-profile.jpg';

const LocalWorkspace = ({ content_id, isLOCALProcessing }) => {
  // plan & credits state
  const [currentPlan, setCurrentPlan]   = useState('free');
  const [creditsUsed, setCreditsUsed]   = useState(0);
  const [creditsLimit, setCreditsLimit] = useState(0);
  const [showPlanPopUp, setShowPlanPopUp] = useState(false);

  // upload state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showPopup, setShowPopup]           = useState(false);
  const [selectedFile, setSelectedFile]     = useState(null);
  const [showInsufficientTimePopup, setShowInsufficientTimePopup] = useState(false);
  const [videoDurationSec, setVideoDurationSec] = useState(null);

  const fileInputRef   = useRef(null);
  const uploadTaskRef  = useRef(null);
  const storagePathRef = useRef('');

  const MAX_FILE_SIZE_GB    = 16;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_GB * 1024 ** 3;

  // derived flags
  const isPro = currentPlan.toLowerCase() === 'pro';
  const remainingCredits = Math.max(creditsLimit - creditsUsed, 0);
  const canUpload = isPro && remainingCredits > 0;

  // 1) fetch & subscribe to planDetails → currentPlan, creditsUsed, creditsLimit
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    const planRef = doc(db, 'users', user.uid, 'subscriptionData', 'planDetails');

    // initial fetch
    getDoc(planRef)
      .then(snap => {
        if (snap.exists()) {
          const data = snap.data();
          setCurrentPlan((data.currentPlan || 'free').toLowerCase());
          setCreditsUsed(data.creditsUsed ?? 0);
          setCreditsLimit(data.creditsLimit ?? 0);
        }
      })
      .catch(() => {});

    // realtime updates
    const unsub = onSnapshot(planRef, snap => {
      if (snap.exists()) {
        const data = snap.data();
        setCurrentPlan((data.currentPlan || 'free').toLowerCase());
        setCreditsUsed(data.creditsUsed ?? 0);
        setCreditsLimit(data.creditsLimit ?? 0);
      }
    });

    return () => unsub();
  }, []);

  // 2) handleUpload must be declared before drag-and-drop effect
  const handleUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      alert(`The selected video file exceeds ${MAX_FILE_SIZE_GB} GB. Please select a smaller file.`);
      return;
    }

    const videoEl = document.createElement('video');
    videoEl.preload = 'metadata';
    videoEl.onloadedmetadata = () => {
      window.URL.revokeObjectURL(videoEl.src);
      const duration = videoEl.duration;
      setVideoDurationSec(duration);

      const availableSec = remainingCredits * 60;
      if (duration > availableSec) {
        setShowInsufficientTimePopup(true);
        return;
      }

      setSelectedFile(file);
      const user = auth.currentUser;
      uploadlocalWorkspace(
        file,
        user,
        content_id,
        setUploadProgress,
        setShowPopup,
        storagePathRef,
        uploadTaskRef
      );
      event.target.value = null;
    };
    videoEl.src = URL.createObjectURL(file);
  }, [remainingCredits, content_id, MAX_FILE_SIZE_BYTES]);

  // 3) drag-and-drop effect, gated on canUpload
  useEffect(() => {
    const dropArea = document.querySelector('.localwork-file-drop-area');
    if (!dropArea) return;

    const onDragOver = e => {
      e.preventDefault(); e.stopPropagation();
      if (!canUpload) return;
      const it = e.dataTransfer.items;
      if (it.length > 0 && it[0].kind === 'file' && it[0].type.startsWith('video/')) {
        dropArea.classList.add('drag-over');
      }
    };
    const onDragLeave = e => {
      e.preventDefault(); e.stopPropagation();
      if (!canUpload) return;
      dropArea.classList.remove('drag-over');
    };
    const onDrop = e => {
      e.preventDefault(); e.stopPropagation();
      if (!canUpload) return;
      dropArea.classList.remove('drag-over');
      const file = e.dataTransfer.files[0];
      if (file) handleUpload({ target: { files: [file] } });
    };

    dropArea.addEventListener('dragover', onDragOver);
    dropArea.addEventListener('dragleave', onDragLeave);
    dropArea.addEventListener('drop', onDrop);
    return () => {
      dropArea.removeEventListener('dragover', onDragOver);
      dropArea.removeEventListener('dragleave', onDragLeave);
      dropArea.removeEventListener('drop', onDrop);
    };
  }, [canUpload, handleUpload]);

  const triggerFileInput = () => fileInputRef.current?.click();

  return (
    <div className="localwork-upload-section">
      <div className={`localwork-upload-area ${!canUpload ? 'disabled' : ''}`}>
        <label htmlFor="fileInput" className="localwork-file-input-label">
          <div className="localwork-file-drop-area">
            <input
              type="file"
              id="fileInput"
              ref={fileInputRef}
              accept="video/mp4,video/mov,video/webm,video/quicktime"
              onChange={handleUpload}
              hidden
              disabled={!canUpload}
            />

            <div className="localwork-upload-circle">
              <FontAwesomeIcon
                icon={faCircleArrowUp}
                className="upload-logo"
                title="Upload"
              />
            </div>

            {!isPro ? (
              <>
                <p className="localwork-upload-instruction">
                  You need the <b>Pro plan</b> for local uploads.
                </p>
                <p className="localwork-upload-subtext">
                  Upgrade to enable local video uploads, advanced vision capabilities, and more.
                </p>
              </>
            ) : remainingCredits === 0 ? (
              <>
                <p className="localwork-upload-instruction">
                  You are currently out of credits for this month
                </p>
                <p className="localwork-upload-subtext">
                  Next billing cycle you will receive {creditsLimit} credits.
                </p>
              </>
            ) : (
              <>
                <p className="localwork-upload-instruction">
                  Drag and drop video files to upload
                </p>
                <p className="localwork-upload-subtext">
                  Your videos will be private until you publish them.
                </p>
              </>
            )}

            {(!isPro || remainingCredits === 0) ? (
              <button
                className="localwork-select-files-button"
                onClick={() => setShowPlanPopUp(true)}
              >
                View Plan
              </button>
            ) : (
              <button
                className="localwork-select-files-button"
                onClick={triggerFileInput}
              >
                <img
                  src={bumpups_white_small_logo}
                  alt="Bump Plan Logo"
                  className="localwork-icon"
                />
                Select files
              </button>
            )}
          </div>
        </label>
      </div>

      {showPopup && (
        <UploadPopup
          progress={uploadProgress}
          file={selectedFile}
          onClose={() => setShowPopup(false)}
          uploadTaskRef={uploadTaskRef}
          storagePathRef={storagePathRef}
          content_id={content_id}
          videoDurationSec={videoDurationSec}
          isLOCALProcessing={isLOCALProcessing}
          remainingCredits={remainingCredits}
        />
      )}

      {showInsufficientTimePopup && <InsufficientTimePopUp />}

      {showPlanPopUp && (
        <PlanPurchasePage
          isOpen={showPlanPopUp}
          onClose={() => setShowPlanPopUp(false)}
        />
      )}
    </div>
  );
};

export default LocalWorkspace;

// Nested Components

function UploadPopup({
  progress,
  file,
  onClose,
  uploadTaskRef,
  storagePathRef,
  content_id,
  videoDurationSec,
  isLOCALProcessing,
  remainingCredits
}) {
  const [videoUrl, setVideoUrl] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [displayedProgress, setDisplayedProgress] = useState(progress);
  const [uploadStatusText, setUploadStatusText] = useState(`Uploading (${progress}%)`);
  const [profileImage, setProfileImage] = useState(placeholder_profile);
  const [isOutOfTime, setIsOutOfTime] = useState(false);

  const user = auth.currentUser;

  // preview video
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setVideoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // progress text
  useEffect(() => {
    if (progress < 98) {
      setDisplayedProgress(progress);
      setUploadStatusText(`Uploading (${progress}%)`);
    } else if (progress < 100) {
      setDisplayedProgress(98);
      setUploadStatusText('Uploading (98%)');
    } else {
      setDisplayedProgress(100);
      setUploadStatusText('Finalizing Upload...');
    }
  }, [progress]);

  // cancel if credits drop to zero mid-upload
  useEffect(() => {
    const neededSec = videoDurationSec || 0;
    if (remainingCredits * 60 < neededSec && progress < 100) {
      setIsOutOfTime(true);
      uploadTaskRef.current?.cancel();
    } else {
      setIsOutOfTime(false);
    }
  }, [videoDurationSec, progress, remainingCredits, uploadTaskRef]);

  // fetch & watch profile image
  useEffect(() => {
    let unsub;
    if (user) {
      const profileRef = doc(db, 'users', user.uid, 'userSettings', 'profile');
      getDoc(profileRef)
        .then(snap => {
          setProfileImage(snap.exists() ? (snap.data().profileImage || placeholder_profile) : placeholder_profile);
        })
        .catch(() => setProfileImage(placeholder_profile));
      unsub = onSnapshot(profileRef, snap => {
        setProfileImage(snap.exists() ? (snap.data().profileImage || placeholder_profile) : placeholder_profile);
      });
    }
    return () => unsub && unsub();
  }, [user]);

  // watch firestore for completion
  useEffect(() => {
    if (!user || !content_id) return;
    const videoDocRef = doc(db, 'users', user.uid, 'workspaceContent', content_id);
    const unsub = onSnapshot(videoDocRef, snap => {
      if (snap.exists() && 'duration' in snap.data()) {
        setUploadStatusText('Upload Complete');
      }
    });
    return () => unsub();
  }, [user, content_id]);

  const handleCancelUpload = () => {
    if (progress === 100 && isLOCALProcessing) {
      localStorage.removeItem(`recentWorkspace_${user.uid}`);
      onClose();
    } else {
      setShowConfirmation(true);
    }
  };

  const [isDiscarding, setIsDiscarding] = useState(false);
  const handleConfirmClose = async () => {
    setIsDiscarding(true);
    if (progress < 100 || !isLOCALProcessing) {
      uploadTaskRef.current?.cancel();
    }
    if (storagePathRef.current) {
      await deleteObject(storagePathRef.current).catch(() => {});
    }
    setShowConfirmation(false);
    onClose();
  };
  const handleCancelClose = () => setShowConfirmation(false);

  useEffect(() => {
    const onError = () => setUploadError(true);
    uploadTaskRef.current?.on('state_changed', null, onError);
  }, [uploadTaskRef]);

  useEffect(() => {
    const beforeUnload = e => {
      if (progress < 100 && !isLOCALProcessing) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [progress, isLOCALProcessing]);

  const truncateFileName = name =>
    name.length > 28 ? `${name.slice(0, 28)}...` : name;

  return (
    <div className="localwork-upload-popup">
      <div className="localwork-upload-popup-content">
        <div className="localwork-upload-header">
          <img src={profileImage} alt="User Profile" className="localwork-prevprofile-image" />
          <button onClick={handleCancelUpload} className="localwork-upload-close-button">X</button>
        </div>
        <div className="localwork-upload-content-wrapper">
          <div className="localwork-video-wrapper" style={{ position: 'relative' }}>
            <video src={videoUrl} controls className="localwork-upload-video" />
          </div>
          <div className="localwork-upload-info-wrapper">
            <div className="localwork-upload-info">
              <p className="localwork-upload-message">
                Keep your browser tab open to avoid interruptions. You may cancel anytime but if your video reaches 100% uploaded, your plan credits will be deducted.
              </p>
              <a
                href="https://intercom.help/bumpups/en/articles/9742215-understanding-the-video-upload-process"
                className="localwork-learn-more-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </div>
          </div>
          <div className={`localwork-upload-status-wrapper ${uploadError || isOutOfTime ? 'error' : ''}`}>
            <div className="localwork-upload-status">
              <p className="localwork-upload-progress">
                <strong>
                  {truncateFileName(file.name)}:{' '}
                  {isOutOfTime
                    ? 'You are out of credits'
                    : (uploadError ? 'Error Uploading -- Retry' : uploadStatusText)
                  }
                </strong>
              </p>
            </div>
          </div>
        </div>
        <div className="localwork-divider" />
        <div className="localwork-upload-buttons">
          <div className="localwork-buttons-right">
            <img
              src={bumpups_white_small_logo}
              alt="Bump Plan Logo"
              className="localwork-bump-icon"
            />
          </div>
        </div>
      </div>
      {showConfirmation && (
        <CancelUploadPopup
          onConfirm={handleConfirmClose}
          onCancel={handleCancelClose}
          isDiscarding={isDiscarding}
        />
      )}
    </div>
  );
}

function CancelUploadPopup({ onConfirm, onCancel, isDiscarding }) {
  return (
    <div className="localwork-cancel-upload-popup" onClick={onCancel}>
      <div className="localwork-cancel-upload-popup-content" onClick={e => e.stopPropagation()}>
        {!isDiscarding && <h2 className="localwork-cancel-upload-title">Hold that thought</h2>}
        {isDiscarding ? (
          <div className="localwork-loading-wrapper">
            <img src={processBumpupslogo} alt="Loading" className="localwork-loading-logo" />
            <span className="localwork-loading-text">Deleting...</span>
          </div>
        ) : (
          <p className="localwork-cancel-upload-message">
            We are still uploading your video. Are you sure you want to discard your post? Your draft will be lost.
          </p>
        )}
        {!isDiscarding && (
          <div className="localwork-cancel-upload-popup-buttons">
            <button onClick={onCancel} className="localwork-cancel-button">Continue</button>
            <button onClick={onConfirm} className="localwork-confirm-button">Discard</button>
          </div>
        )}
      </div>
    </div>
  );
}
